import { Checkbox as ChakraCheckbox } from '@chakra-ui/react';
import * as React from 'react';

import type { CheckboxSize } from './checkbox.kds';

type CheckboxHiddenInputProps = React.ComponentPropsWithoutRef<'input'> &
  Partial<Record<`data-${string}`, string>>;

export interface CheckboxProps extends Omit<ChakraCheckbox.RootProps, 'size'> {
  /** KDS size — `sm` (Small) or `lg` (Large) per Figma. Default `sm`. */
  size?: CheckboxSize;
  /** Custom indicator icon; defaults to Chakra `Checkbox.Indicator`. */
  icon?: React.ReactNode;
  /** Native input attributes (`name`, `readOnly`, `data-*`, etc.). Prefer top-level `aria-label` for accessible names. */
  inputProps?: CheckboxHiddenInputProps;
  /** Ref on the outer `<label>`; `ref`/`forwardRef` targets the hidden input (for forms and libraries). */
  rootRef?: React.Ref<HTMLLabelElement>;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(props, ref) {
  const { icon, children, inputProps, rootRef, size = 'sm', ...rest } = props;

  const indicator = icon ?? <ChakraCheckbox.Indicator />;

  return (
    <ChakraCheckbox.Root ref={rootRef} size={size} {...rest}>
      <ChakraCheckbox.HiddenInput ref={ref} {...inputProps} />
      <ChakraCheckbox.Control data-testid="checkbox-control">
        {size === 'sm' ? <span data-checkbox-control>{indicator}</span> : indicator}
      </ChakraCheckbox.Control>
      {children != null && <ChakraCheckbox.Label>{children}</ChakraCheckbox.Label>}
    </ChakraCheckbox.Root>
  );
});
